import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Building2, Palette, Key, Globe, CheckCircle2, ChevronRight,
  ChevronLeft, Shield, Upload, Loader2, Copy, ExternalLink,
  AlertCircle, Check, X
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, label: "Company Info", icon: Building2 },
  { id: 2, label: "Branding", icon: Palette },
  { id: 3, label: "Identity Provider", icon: Key },
  { id: 4, label: "Verify Domain", icon: Globe },
  { id: 5, label: "Activate", icon: CheckCircle2 },
];

const PROVIDERS = [
  { id: "azure_ad", name: "Microsoft Azure AD", desc: "For Microsoft 365 / Azure organizations", color: "#38BDF8" },
  { id: "google_workspace", name: "Google Workspace", desc: "For Google Workspace / G Suite organizations", color: "#4ADE80" },
  { id: "okta", name: "Okta", desc: "For Okta-managed identity", color: "#A78BFA" },
];

const SSOOnboardingWizard = () => {
  const { t } = useTranslation('platform');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [onboardingId, setOnboardingId] = useState(searchParams.get("id") || "");
  const [copied, setCopied] = useState("");

  // Step 1
  const [companyName, setCompanyName] = useState("");
  const [domain, setDomain] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactName, setContactName] = useState("");

  // Step 2
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoBase64, setLogoBase64] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#C9A84C");

  // Step 3
  const [provider, setProvider] = useState("azure_ad");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [tenantId, setTenantId] = useState("common");
  const [oktaDomain, setOktaDomain] = useState("");
  const [defaultRole, setDefaultRole] = useState("client");
  const [redirectUri, setRedirectUri] = useState("");

  // Step 4
  const [verificationCode, setVerificationCode] = useState("");
  const [verifyMethod, setVerifyMethod] = useState("email");
  const [verifyResult, setVerifyResult] = useState(null);
  const [domainVerified, setDomainVerified] = useState(false);

  // Step 5
  const [activateResult, setActivateResult] = useState(null);

  // Load existing onboarding if ID present
  useEffect(() => {
    if (onboardingId) {
      fetch(`${API}/api/sso-onboarding/${onboardingId}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data) {
            setCompanyName(data.company_name || "");
            setDomain(data.domain || "");
            setContactEmail(data.contact_email || "");
            setContactName(data.contact_name || "");
            setProvider(data.provider || "azure_ad");
            setClientId(data.client_id || "");
            setTenantId(data.tenant_id || "common");
            setOktaDomain(data.okta_domain || "");
            setDefaultRole(data.default_role || "client");
            setVerificationCode(data.verification_code || "");
            setDomainVerified(data.domain_verified || false);
            setPrimaryColor(data.primary_color || "#C9A84C");
            if (data.logo_base64) { setLogoPreview(data.logo_base64); setLogoBase64(data.logo_base64); }
            setStep(Math.min((data.step || 1) + 1, 5));
          }
        })
        .catch(() => {});
    }
  }, [onboardingId]);

  const handleStartOnboarding = async () => {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/api/sso-onboarding/start`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ company_name: companyName, domain, contact_email: contactEmail, contact_name: contactName }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to start onboarding");
      setOnboardingId(data.onboarding_id);
      setVerificationCode(data.verification_code);
      setStep(2);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const handleBranding = async () => {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/api/sso-onboarding/${onboardingId}/branding`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ logo_base64: logoBase64, primary_color: primaryColor }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      setStep(3);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const handleProvider = async () => {
    setLoading(true); setError("");
    try {
      const body = { provider, client_id: clientId, client_secret: clientSecret, default_role: defaultRole };
      if (provider === "azure_ad") body.tenant_id = tenantId;
      if (provider === "okta") body.okta_domain = oktaDomain;
      const res = await fetch(`${API}/api/sso-onboarding/${onboardingId}/provider`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      setRedirectUri(data.redirect_uri);
      setStep(4);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const handleVerifyDomain = async () => {
    setLoading(true); setError(""); setVerifyResult(null);
    try {
      const res = await fetch(`${API}/api/sso-onboarding/${onboardingId}/verify-domain`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ method: verifyMethod }),
      });
      const data = await res.json();
      setVerifyResult(data);
      if (data.verified) { setDomainVerified(true); setStep(5); }
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const handleActivate = async () => {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/api/sso-onboarding/${onboardingId}/activate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (!res.ok) {
        const errs = data.detail?.errors || [data.detail || "Activation failed"];
        throw new Error(Array.isArray(errs) ? errs.join(", ") : errs);
      }
      setActivateResult(data);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setError(t('platform:ssoWizard.logoTooLarge')); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setLogoPreview(ev.target.result);
      setLogoBase64(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(""), 2000);
  };

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white" data-testid="sso-onboarding-wizard">
      {/* Header */}
      <div className="border-b border-[rgba(255,255,255,0.06)] px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield size={24} className="text-[#C9A84C]" />
            <div>
              <h1 className="font-['Syne'] text-lg font-bold">Enterprise SSO Setup</h1>
              <p className="text-[#8B95A5] text-xs">Configure single sign-on for your organization</p>
            </div>
          </div>
          <button onClick={() => navigate("/login")} className="text-[#8B95A5] text-xs hover:text-white">
            Back to Login
          </button>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-3xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-8">
          {STEPS.map((s, i) => {
            const isActive = step === s.id;
            const isDone = step > s.id;
            const Icon = s.icon;
            return (
              <div key={s.id} className="flex items-center">
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  isActive ? "bg-[#C9A84C20] text-[#C9A84C]" :
                  isDone ? "bg-[#4ADE8020] text-[#4ADE80]" :
                  "text-[#555]"
                }`}>
                  {isDone ? <Check size={14} /> : <Icon size={14} />}
                  <span className="hidden sm:inline">{s.label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <ChevronRight size={14} className={`mx-1 ${isDone ? "text-[#4ADE80]" : "text-[#333]"}`} />
                )}
              </div>
            );
          })}
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-4 p-3 bg-[#EF444415] border border-[#EF444430] rounded flex items-start gap-2" data-testid="wizard-error">
            <AlertCircle size={16} className="text-[#EF4444] mt-0.5 flex-shrink-0" />
            <p className="text-[#EF4444] text-sm">{error}</p>
            <button onClick={() => setError("")} className="ml-auto text-[#EF4444]"><X size={14} /></button>
          </div>
        )}

        {/* ============== STEP 1: COMPANY INFO ============== */}
        {step === 1 && (
          <div className="qms-card" data-testid="step-1">
            <div className="qms-card-body space-y-4">
              <h2 className="text-white font-semibold text-base mb-1">Company Information</h2>
              <p className="text-[#8B95A5] text-xs mb-4">Tell us about your organization</p>
              <div>
                <label className="text-[#8B95A5] text-[10px] uppercase mb-1 block">Company Name *</label>
                <input value={companyName} onChange={e => setCompanyName(e.target.value)}
                  placeholder="Acme Corporation" data-testid="input-company-name"
                  className="w-full bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2.5 text-white text-sm focus:border-[#C9A84C] focus:outline-none" />
              </div>
              <div>
                <label className="text-[#8B95A5] text-[10px] uppercase mb-1 block">Company Domain *</label>
                <input value={domain} onChange={e => setDomain(e.target.value.toLowerCase())}
                  placeholder="acme.com" data-testid="input-domain"
                  className="w-full bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2.5 text-white text-sm focus:border-[#C9A84C] focus:outline-none" />
                <p className="text-[#555] text-[10px] mt-1">Employees with @{domain || "company.com"} emails will use this SSO</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[#8B95A5] text-[10px] uppercase mb-1 block">Contact Name</label>
                  <input value={contactName} onChange={e => setContactName(e.target.value)}
                    placeholder="Jane Smith" data-testid="input-contact-name"
                    className="w-full bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2.5 text-white text-sm focus:border-[#C9A84C] focus:outline-none" />
                </div>
                <div>
                  <label className="text-[#8B95A5] text-[10px] uppercase mb-1 block">Contact Email *</label>
                  <input value={contactEmail} onChange={e => setContactEmail(e.target.value.toLowerCase())}
                    placeholder="jane@acme.com" data-testid="input-contact-email"
                    className="w-full bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2.5 text-white text-sm focus:border-[#C9A84C] focus:outline-none" />
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <button onClick={handleStartOnboarding}
                  disabled={loading || !companyName || !domain || !contactEmail}
                  className="flex items-center gap-1.5 px-5 py-2.5 bg-[#C9A84C] text-[#0B0E14] rounded text-xs font-semibold hover:bg-[#D4B85C] disabled:opacity-40"
                  data-testid="btn-next-step1">
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <ChevronRight size={14} />}
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ============== STEP 2: BRANDING ============== */}
        {step === 2 && (
          <div className="qms-card" data-testid="step-2">
            <div className="qms-card-body space-y-4">
              <h2 className="text-white font-semibold text-base mb-1">Company Branding</h2>
              <p className="text-[#8B95A5] text-xs mb-4">Optional — customize the login experience for your employees</p>
              <div>
                <label className="text-[#8B95A5] text-[10px] uppercase mb-2 block">Company Logo</label>
                <div className="flex items-center gap-4">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo" className="w-16 h-16 rounded object-contain bg-[#1A1D27] p-1" />
                  ) : (
                    <div className="w-16 h-16 rounded bg-[#1A1D27] flex items-center justify-center text-[#555]">
                      <Upload size={20} />
                    </div>
                  )}
                  <label className="cursor-pointer px-3 py-2 border border-[rgba(255,255,255,0.08)] rounded text-xs text-[#8B95A5] hover:text-white hover:border-[#C9A84C]">
                    Upload Logo (PNG, JPG, SVG — max 2MB)
                    <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="logo-upload" />
                  </label>
                </div>
              </div>
              <div>
                <label className="text-[#8B95A5] text-[10px] uppercase mb-1 block">Primary Color</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer bg-transparent border-0" data-testid="color-picker" />
                  <input value={primaryColor} onChange={e => setPrimaryColor(e.target.value)}
                    className="bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2 text-white text-sm w-28" />
                </div>
              </div>
              <div className="flex justify-between pt-2">
                <button onClick={() => setStep(1)} className="flex items-center gap-1 text-[#8B95A5] text-xs hover:text-white">
                  <ChevronLeft size={14} /> Back
                </button>
                <div className="flex gap-2">
                  <button onClick={() => setStep(3)} className="text-[#8B95A5] text-xs hover:text-white px-3 py-2">Skip</button>
                  <button onClick={handleBranding} disabled={loading}
                    className="flex items-center gap-1.5 px-5 py-2.5 bg-[#C9A84C] text-[#0B0E14] rounded text-xs font-semibold hover:bg-[#D4B85C]"
                    data-testid="btn-next-step2">
                    {loading ? <Loader2 size={14} className="animate-spin" /> : <ChevronRight size={14} />}
                    Continue
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============== STEP 3: IDENTITY PROVIDER ============== */}
        {step === 3 && (
          <div className="qms-card" data-testid="step-3">
            <div className="qms-card-body space-y-4">
              <h2 className="text-white font-semibold text-base mb-1">Identity Provider</h2>
              <p className="text-[#8B95A5] text-xs mb-4">Select and configure your company's identity provider</p>

              <div className="grid gap-2">
                {PROVIDERS.map(p => (
                  <button key={p.id} onClick={() => setProvider(p.id)}
                    className={`flex items-center gap-3 p-3 rounded border text-left transition-colors ${
                      provider === p.id
                        ? "border-[#C9A84C] bg-[#C9A84C08]"
                        : "border-[rgba(255,255,255,0.06)] hover:border-[rgba(255,255,255,0.15)]"
                    }`} data-testid={`provider-${p.id}`}>
                    <div className="w-3 h-3 rounded-full border-2" style={{
                      borderColor: provider === p.id ? "#C9A84C" : "#555",
                      backgroundColor: provider === p.id ? "#C9A84C" : "transparent",
                    }} />
                    <div>
                      <p className="text-white text-sm font-medium">{p.name}</p>
                      <p className="text-[#8B95A5] text-[10px]">{p.desc}</p>
                    </div>
                  </button>
                ))}
              </div>

              <div className="pt-2 space-y-3">
                <div>
                  <label className="text-[#8B95A5] text-[10px] uppercase mb-1 block">Application (Client) ID *</label>
                  <input value={clientId} onChange={e => setClientId(e.target.value)}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" data-testid="input-client-id"
                    className="w-full bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2.5 text-white text-sm font-mono focus:border-[#C9A84C] focus:outline-none" />
                </div>
                <div>
                  <label className="text-[#8B95A5] text-[10px] uppercase mb-1 block">Client Secret *</label>
                  <input type="password" value={clientSecret} onChange={e => setClientSecret(e.target.value)}
                    placeholder="Your client secret" data-testid="input-client-secret"
                    className="w-full bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2.5 text-white text-sm focus:border-[#C9A84C] focus:outline-none" />
                </div>
                {provider === "azure_ad" && (
                  <div>
                    <label className="text-[#8B95A5] text-[10px] uppercase mb-1 block">Tenant ID</label>
                    <input value={tenantId} onChange={e => setTenantId(e.target.value)}
                      placeholder="common (or your directory tenant ID)" data-testid="input-tenant-id"
                      className="w-full bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2.5 text-white text-sm font-mono focus:border-[#C9A84C] focus:outline-none" />
                  </div>
                )}
                {provider === "okta" && (
                  <div>
                    <label className="text-[#8B95A5] text-[10px] uppercase mb-1 block">Okta Domain</label>
                    <input value={oktaDomain} onChange={e => setOktaDomain(e.target.value)}
                      placeholder="your-company.okta.com" data-testid="input-okta-domain"
                      className="w-full bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2.5 text-white text-sm focus:border-[#C9A84C] focus:outline-none" />
                  </div>
                )}
                <div>
                  <label className="text-[#8B95A5] text-[10px] uppercase mb-1 block">Default Role for New Users</label>
                  <select value={defaultRole} onChange={e => setDefaultRole(e.target.value)}
                    className="w-full bg-[#1A1D27] border border-[rgba(255,255,255,0.08)] rounded px-3 py-2.5 text-white text-sm"
                    data-testid="input-default-role">
                    <option value="client">Client</option>
                    <option value="auditor">PM / Auditor</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-between pt-2">
                <button onClick={() => setStep(2)} className="flex items-center gap-1 text-[#8B95A5] text-xs hover:text-white">
                  <ChevronLeft size={14} /> Back
                </button>
                <button onClick={handleProvider} disabled={loading || !clientId || !clientSecret}
                  className="flex items-center gap-1.5 px-5 py-2.5 bg-[#C9A84C] text-[#0B0E14] rounded text-xs font-semibold hover:bg-[#D4B85C] disabled:opacity-40"
                  data-testid="btn-next-step3">
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <ChevronRight size={14} />}
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ============== STEP 4: DOMAIN VERIFICATION ============== */}
        {step === 4 && (
          <div className="qms-card" data-testid="step-4">
            <div className="qms-card-body space-y-4">
              <h2 className="text-white font-semibold text-base mb-1">Verify Domain Ownership</h2>
              <p className="text-[#8B95A5] text-xs mb-4">Prove you own <span className="text-white">@{domain}</span> to activate SSO</p>

              {/* Redirect URI info */}
              {redirectUri && (
                <div className="p-3 bg-[#38BDF810] border border-[#38BDF830] rounded">
                  <p className="text-[#38BDF8] text-[10px] uppercase font-medium mb-1">Important: Add this Redirect URI to your IdP</p>
                  <div className="flex items-center gap-2">
                    <code className="text-white text-xs bg-[#0B0E14] px-2 py-1 rounded flex-1 truncate">{redirectUri}</code>
                    <button onClick={() => copyToClipboard(redirectUri, "uri")}
                      className="p-1.5 rounded hover:bg-[#38BDF820] text-[#38BDF8]">
                      {copied === "uri" ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  </div>
                </div>
              )}

              {/* Verification method */}
              <div className="space-y-2">
                <label className="text-[#8B95A5] text-[10px] uppercase mb-1 block">Verification Method</label>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setVerifyMethod("email")}
                    className={`p-3 rounded border text-left ${verifyMethod === "email" ? "border-[#C9A84C] bg-[#C9A84C08]" : "border-[rgba(255,255,255,0.06)]"}`}
                    data-testid="verify-email">
                    <p className="text-white text-xs font-medium">Email Verification</p>
                    <p className="text-[#8B95A5] text-[10px]">Your contact email must be @{domain}</p>
                  </button>
                  <button onClick={() => setVerifyMethod("dns")}
                    className={`p-3 rounded border text-left ${verifyMethod === "dns" ? "border-[#C9A84C] bg-[#C9A84C08]" : "border-[rgba(255,255,255,0.06)]"}`}
                    data-testid="verify-dns">
                    <p className="text-white text-xs font-medium">DNS TXT Record</p>
                    <p className="text-[#8B95A5] text-[10px]">Add a TXT record to your DNS</p>
                  </button>
                </div>
              </div>

              {verifyMethod === "dns" && (
                <div className="p-3 bg-[#1A1D27] rounded space-y-2">
                  <p className="text-[#8B95A5] text-xs">Add this TXT record to your DNS:</p>
                  <div className="grid grid-cols-[80px_1fr] gap-2 text-xs">
                    <span className="text-[#8B95A5]">Type:</span><span className="text-white">TXT</span>
                    <span className="text-[#8B95A5]">Name:</span>
                    <div className="flex items-center gap-1">
                      <code className="text-[#C9A84C]">_charstan-verify.{domain}</code>
                      <button onClick={() => copyToClipboard(`_charstan-verify.${domain}`, "dns-name")} className="text-[#8B95A5]">
                        {copied === "dns-name" ? <Check size={12} /> : <Copy size={12} />}
                      </button>
                    </div>
                    <span className="text-[#8B95A5]">Value:</span>
                    <div className="flex items-center gap-1">
                      <code className="text-[#4ADE80] break-all">{verificationCode}</code>
                      <button onClick={() => copyToClipboard(verificationCode, "dns-val")} className="text-[#8B95A5]">
                        {copied === "dns-val" ? <Check size={12} /> : <Copy size={12} />}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Verification result */}
              {verifyResult && (
                <div className={`p-3 rounded border ${verifyResult.verified
                  ? "bg-[#4ADE8010] border-[#4ADE8030] text-[#4ADE80]"
                  : "bg-[#EF444410] border-[#EF444430] text-[#EF4444]"
                } text-xs`} data-testid="verify-result">
                  {verifyResult.verified ? <CheckCircle2 size={14} className="inline mr-1" /> : <AlertCircle size={14} className="inline mr-1" />}
                  {verifyResult.message}
                </div>
              )}

              <div className="flex justify-between pt-2">
                <button onClick={() => setStep(3)} className="flex items-center gap-1 text-[#8B95A5] text-xs hover:text-white">
                  <ChevronLeft size={14} /> {t('platform:ssoWizard.back')}
                </button>
                <button onClick={domainVerified ? () => setStep(5) : handleVerifyDomain} disabled={loading}
                  className="flex items-center gap-1.5 px-5 py-2.5 bg-[#C9A84C] text-[#0B0E14] rounded text-xs font-semibold hover:bg-[#D4B85C]"
                  data-testid="btn-verify">
                  {loading ? <Loader2 size={14} className="animate-spin" /> : domainVerified ? <ChevronRight size={14} /> : <Globe size={14} />}
                  {domainVerified ? t('platform:ssoWizard.continue') : t('platform:ssoWizard.verifyDomain')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ============== STEP 5: ACTIVATE ============== */}
        {step === 5 && !activateResult && (
          <div className="qms-card" data-testid="step-5">
            <div className="qms-card-body space-y-4">
              <h2 className="text-white font-semibold text-base mb-1">{t('platform:ssoWizard.reviewActivate')}</h2>
              <p className="text-[#8B95A5] text-xs mb-4">{t('platform:ssoWizard.reviewSubtitle')}</p>

              <div className="space-y-2 text-xs">
                <div className="flex justify-between p-2 bg-[#1A1D27] rounded">
                  <span className="text-[#8B95A5]">{t('platform:ssoWizard.company')}</span><span className="text-white">{companyName}</span>
                </div>
                <div className="flex justify-between p-2 bg-[#1A1D27] rounded">
                  <span className="text-[#8B95A5]">{t('platform:ssoWizard.domain')}</span><span className="text-white">@{domain}</span>
                </div>
                <div className="flex justify-between p-2 bg-[#1A1D27] rounded">
                  <span className="text-[#8B95A5]">{t('platform:ssoWizard.provider')}</span>
                  <span className="text-white">{PROVIDERS.find(p => p.id === provider)?.name}</span>
                </div>
                <div className="flex justify-between p-2 bg-[#1A1D27] rounded">
                  <span className="text-[#8B95A5]">{t('platform:ssoWizard.defaultRole')}</span><span className="text-white">{defaultRole}</span>
                </div>
                <div className="flex justify-between p-2 bg-[#1A1D27] rounded">
                  <span className="text-[#8B95A5]">{t('platform:ssoWizard.domainVerified')}</span>
                  <span className={domainVerified ? "text-[#4ADE80]" : "text-[#EF4444]"}>
                    {domainVerified ? t('platform:ssoWizard.yes') : t('platform:ssoWizard.noGoBack')}
                  </span>
                </div>
              </div>

              <div className="flex justify-between pt-2">
                <button onClick={() => setStep(4)} className="flex items-center gap-1 text-[#8B95A5] text-xs hover:text-white">
                  <ChevronLeft size={14} /> {t('platform:ssoWizard.back')}
                </button>
                <button onClick={handleActivate} disabled={loading || !domainVerified}
                  className="flex items-center gap-1.5 px-6 py-2.5 bg-[#4ADE80] text-[#0B0E14] rounded text-xs font-bold hover:bg-[#5AEE90] disabled:opacity-40"
                  data-testid="btn-activate">
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                  {t('platform:ssoWizard.activate')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ============== SUCCESS ============== */}
        {activateResult && (
          <div className="qms-card" data-testid="activation-success">
            <div className="qms-card-body text-center py-10">
              <div className="w-16 h-16 rounded-full bg-[#4ADE8020] flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={32} className="text-[#4ADE80]" />
              </div>
              <h2 className="text-white font-['Syne'] text-xl font-bold mb-2">SSO Activated!</h2>
              <p className="text-[#8B95A5] text-sm max-w-md mx-auto mb-6">{activateResult.message}</p>
              <div className="p-4 bg-[#1A1D27] rounded-lg max-w-md mx-auto text-left mb-6">
                <p className="text-[#C9A84C] text-[10px] uppercase font-medium mb-2">How your employees sign in:</p>
                <ol className="text-[#CCC] text-xs space-y-1.5 list-decimal list-inside">
                  <li>Go to the CHARSTAN login page</li>
                  <li>Click <span className="text-[#C9A84C] font-medium">"Company Email / Enterprise SSO"</span></li>
                  <li>Enter their <span className="text-white">@{domain}</span> email</li>
                  <li>Authenticate through your company's login portal</li>
                </ol>
              </div>
              <div className="flex justify-center gap-3">
                <button onClick={() => navigate("/login")}
                  className="px-4 py-2 bg-[#C9A84C] text-[#0B0E14] rounded text-xs font-semibold hover:bg-[#D4B85C]"
                  data-testid="btn-go-login">
                  Go to Login
                </button>
                <button onClick={() => navigate("/enterprise-sso")}
                  className="px-4 py-2 border border-[rgba(255,255,255,0.08)] rounded text-xs text-white hover:bg-[#1A1D27]">
                  Manage SSO
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SSOOnboardingWizard;
